import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Output, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  IsActiveMatchOptions,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import {
  NAVIGATION,
  NavigationItem,
} from '@/app/domains/admin/layout/data/navigation-tdusa';

/**
 * Sidebar navigation, styled to match the truckdriversus.com landing site menu.
 *
 * Items with children render as accordions (all collapsed by default); items without
 * children render as plain links. A section with its own route/link renders the label as a
 * link and only the chevron toggles it, like the landing site's Jobs and News headers. Accordions are plain buttons with aria-expanded rather than
 * an @angular/aria tree — the tree's roving focus fought the mobile drawer's focus trap in Safari.
 */
@Component({
  selector: 'navigation',
  imports: [
    MatIcon,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
  ],
  template: `
    <nav aria-label="Main" class="flex flex-col px-6">
      @for (section of navigation(); track section.id) {
        <div class="border-b border-neutral-200 last:border-b-0">
          <ng-template
            [ngTemplateOutlet]="node"
            [ngTemplateOutletContext]="{ item: section, top: true }"
          />
        </div>
      }
    </nav>

    <!-- A single item: accordion (has children) or link -->
    <ng-template let-item="item" let-top="top" #node>
      @if (item.children?.length) {
        @if (item.route || item.link) {
          <!-- Header is itself a link; only the chevron expands/collapses -->
          <div class="flex items-center justify-between gap-2">
            <ng-template
              [ngTemplateOutlet]="link"
              [ngTemplateOutletContext]="{ item: item, top: top }"
            />
            <button
              type="button"
              class="-mr-2.5 flex size-10 shrink-0 cursor-pointer items-center justify-center text-neutral-900"
              [attr.aria-label]="(isOpen(item.id) ? 'Collapse ' : 'Expand ') + item.label"
              [attr.aria-expanded]="isOpen(item.id)"
              [attr.aria-controls]="'nav-group-' + item.id"
              (click)="toggle(item.id)"
            >
              <mat-icon
                svgIcon="chevron-down"
                class="size-5 transition-transform duration-200"
                [class.rotate-180]="isOpen(item.id)"
              />
            </button>
          </div>
        } @else {
          <button
            type="button"
            class="flex w-full cursor-pointer items-center justify-between text-left text-lg font-light text-neutral-900"
            [class.py-4]="top"
            [class.py-2]="!top"
            [attr.aria-expanded]="isOpen(item.id)"
            [attr.aria-controls]="'nav-group-' + item.id"
            (click)="toggle(item.id)"
          >
            {{ item.label }}
            <mat-icon
              svgIcon="chevron-down"
              class="size-5 shrink-0 transition-transform duration-200"
              [class.rotate-180]="isOpen(item.id)"
            />
          </button>
        }

        <!-- Collapsible body: animates via grid rows; inert while collapsed so links aren't focusable -->
        <div
          class="grid transition-[grid-template-rows] duration-200 ease-out"
          [id]="'nav-group-' + item.id"
          [style.grid-template-rows]="isOpen(item.id) ? '1fr' : '0fr'"
          [attr.inert]="isOpen(item.id) ? null : ''"
        >
          <!-- min-h-0 + overflow-hidden on a padding-free wrapper so nothing peeks out while collapsed -->
          <div class="min-h-0 overflow-hidden">
            <ul class="flex flex-col" [class.pb-3]="top" [class.pl-4]="!top">
              @for (child of item.children; track child.id) {
                <li>
                  <ng-template
                    [ngTemplateOutlet]="node"
                    [ngTemplateOutletContext]="{ item: child, top: false }"
                  />
                </li>
              }
            </ul>
          </div>
        </div>
      } @else if (item.link || (item.route && !item.disabled)) {
        <ng-template
          [ngTemplateOutlet]="link"
          [ngTemplateOutletContext]="{ item: item, top: top }"
        />
      } @else {
        <div class="text-lg font-light text-neutral-400" [class.py-4]="top" [class.py-2]="!top">
          {{ item.label }}
        </div>
      }
    </ng-template>

    <!-- A link: external (href) or internal route -->
    <ng-template let-item="item" let-top="top" #link>
      @if (item.link) {
        <!-- External link -->
        <a
          class="block flex-auto text-lg font-light text-neutral-900 hover:text-primary-600"
          [class.py-4]="top"
          [class.py-2]="!top"
          [href]="item.link"
          (click)="navItemSelected.emit()"
        >
          {{ item.label }}
        </a>
      } @else if (item.route && !item.disabled) {
        <!-- Internal route -->
        <a
          class="block flex-auto text-lg font-light text-neutral-900 hover:text-primary-600"
          [class.py-4]="top"
          [class.py-2]="!top"
          routerLinkActive="font-normal! text-primary-600!"
          ariaCurrentWhenActive="page"
          [routerLink]="item.route"
          [routerLinkActiveOptions]="isActiveOption(item.activeOptions ?? { exact: true })"
          (click)="navItemSelected.emit()"
        >
          {{ item.label }}
        </a>
      }
    </ng-template>
  `,
})
export class Navigation {
  // Outputs
  @Output() navItemSelected = new EventEmitter<void>();

  // State
  protected navigation = signal<NavigationItem[]>(NAVIGATION);
  private openGroups = signal<ReadonlySet<string>>(new Set());

  isOpen(id: string): boolean {
    return this.openGroups().has(id);
  }

  toggle(id: string): void {
    this.openGroups.update((open) => {
      const next = new Set(open);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  /**
   * Convert simple exact option to full IsActiveMatchOptions.
   * @param options
   */
  isActiveOption(
    options: { exact: boolean } | IsActiveMatchOptions
  ): IsActiveMatchOptions {
    if ('exact' in options) {
      return options.exact
        ? {
          paths: 'exact',
          queryParams: 'exact',
          fragment: 'ignored',
          matrixParams: 'ignored',
        }
        : {
          paths: 'subset',
          queryParams: 'subset',
          fragment: 'ignored',
          matrixParams: 'ignored',
        };
    }

    return options;
  }
}
